import { UzButton } from '@utazzo/ui';
import { X } from 'lucide-react';
import React from 'react';

interface PackageFormPanelProps {
  title: string;
  description?: string;
  pending: boolean;
  submitLabel: string;
  onClose: () => void;
  onSubmit: () => void;
  children: React.ReactNode;
}

/**
 * Inline accordion-style form panel used by the package child editors (days,
 * hotels, flights, FAQs) in place of a side drawer. Renders within the page
 * flow - header + form body + Save/Cancel footer - so adding/editing a row
 * expands inline rather than sliding in from the side.
 */
export const PackageFormPanel = ({
  title,
  description,
  pending,
  submitLabel,
  onClose,
  onSubmit,
  children,
}: PackageFormPanelProps): React.JSX.Element => (
  <div className="rounded-lg border border-zinc-200 bg-zinc-50/50 p-4 sm:p-5">
    <div className="mb-4 flex items-start justify-between gap-3">
      <div>
        <h4 className="text-sm font-semibold text-zinc-900">{title}</h4>
        {description && <p className="mt-0.5 text-xs text-zinc-500">{description}</p>}
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
    {children}
    <div className="mt-5 flex justify-end gap-2">
      <UzButton variant="ghost" onClick={onClose} disabled={pending}>
        Cancel
      </UzButton>
      <UzButton onClick={onSubmit} loading={pending}>
        {submitLabel}
      </UzButton>
    </div>
  </div>
);
